/**
 * AdditionalPostGrid
 *
 * Manages the additional post grid filtering, sorting, and load more functionality
 *
 * @package Gravity
 * @since 1.0.0
 */
/* eslint-disable no-undef, camelcase, radix */
import LoadingManager from '../../../FE/src/assets/javascripts/utils/LoadingManager.js';

export default class AdditionalPostGrid {
  constructor(el) {
    // Main element
    this.$el = $(el);

    // Cache DOM elements
    this.$grid = this.$el.find('.gg-additional-post-grid__grid');
    this.$loadMoreBtn = this.$el.find('.gg-additional-post-grid__load-more');
    this.$loadMoreWrapper = this.$el.find(
      '.gg-additional-post-grid__load-more-wrapper',
    );
    this.$content = this.$el.find('.gg-additional-post-grid__content');
    this.$empty = this.$el.find('.gg-additional-post-grid__empty');
    this.$sortControl = this.$el.find('.gg-additional-post-grid__sort');
    this.$topicFilter = this.$el.find('.gg-additional-post-grid__filter');
    this.$clearFiltersBtn = this.$el.find('.gg-filter-controls__clear-btn');
    this.$searchResults = $('.gg-additional-post-grid__search-results');

    // State
    this.currentPage = 1;
    this.postsPerPage = parseInt(this.$el.data('posts-per-page')) || 6;
    this.maxPages = parseInt(this.$el.data('max-pages')) || 1;
    this.postType = this.$el.data('post-type') || 'news';
    this.pageId = parseInt(this.$el.data('page-id')) || 0;
    this.isLoading = false;

    // Filter state from URL
    this.currentCategory = this.getUrlParameter('category') || '';
    this.currentTopic = this.getUrlParameter('topic') || '';
    this.currentSort = this.getUrlParameter('sort') || '';
    this.currentSearch = this.getUrlParameter('keywords') || '';
    this.excludedIds = [];
  }

  init() {
    this.bindEvents();
  }

  /**
   * Get URL parameter value
   * @param {string} name - Parameter name
   * @returns {string|null} Parameter value or null
   */
  getUrlParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  /**
   * Set URL parameter and update browser history
   * @param {string} name - Parameter name
   * @param {string} value - Parameter value
   */
  setUrlParameter(name, value) {
    const urlParams = new URLSearchParams(window.location.search);

    // Remove parameter if value is 'all' or empty
    if (value === 'all' || value === '' || !value) {
      urlParams.delete(name);
    } else {
      urlParams.set(name, value);
    }

    const queryString = urlParams.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    window.history.pushState({}, '', newUrl);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    $(document).on('filterTabsChanged', (_e, category, _postType) => {
      this.currentCategory = category;
      this.currentPage = 1;
      this.$loadMoreBtn.data('page', 1);
      this.refreshPosts();
    });

    // Load more button
    this.$loadMoreBtn.on('click', () => this.loadMore());

    // Clear filters button
    this.$clearFiltersBtn.on('click', () => this.handleClearFilters());

    // Listen for filter changes to update excluded IDs
    $(document).on('filterApplied', (_e, data) => {
      if (data && data.excludedIds) {
        this.excludedIds = data.excludedIds;
        this.currentCategory = data.category || '';
      }
    });

    // Listen for sort changes
    $(document).on('postGridSortChanged', (_e, value) => {
      this.currentSort = value;
      this.setUrlParameter('sort', value);
      this.refreshPosts();
    });

    // Listen for filter changes
    $(document).on('postGridTopicChanged', (_e, value) => {
      this.currentTopic = value;
      this.setUrlParameter('topic', value);
      this.refreshPosts();
    });

    // Listen for search from news hero banner
    $(document).on('searchFromBanner', (_e, searchQuery) => {
      this.currentSearch = searchQuery;
      this.setUrlParameter('keywords', searchQuery);
      this.refreshPosts();
    });
  }

  /**
   * Handle clear filters button click
   */
  handleClearFilters() {
    this.currentTopic = '';
    this.currentSort = '';
    if (this.$topicFilter.data('ggSelect')) {
      this.$topicFilter.data('ggSelect').reset();
    }
    if (this.$sortControl.data('ggSelect')) {
      this.$sortControl.data('ggSelect').reset();
    }
    this.setUrlParameter('topic', '');
    this.setUrlParameter('sort', '');
    this.refreshPosts();
  }

  /**
   * Update search heading with result count
   * @param {string} searchQuery - Search query string
   * @param {number} count - Number of results
   */
  updateSearchHeadingWithCount(searchQuery, count) {
    if (!this.$searchResults.length) {
      return;
    }

    if (searchQuery) {
      this.$searchResults.removeClass(AdditionalPostGrid.CLASSES.HIDDEN);

      // Update count
      const $countSpan = this.$searchResults.find('.search-results-count');
      if ($countSpan.length) {
        $countSpan.text(count);
      }

      // Update search query
      const $querySpan = this.$searchResults.find('.search-results-query');
      if ($querySpan.length) {
        $querySpan.text(searchQuery);
      }
    } else {
      this.$searchResults.addClass(AdditionalPostGrid.CLASSES.HIDDEN);
    }
  }

  /**
   * Get base AJAX configuration
   * @returns {object} Base AJAX config
   */
  getAjaxBaseConfig() {
    return {
      url: window.ajaxurl || '/wp-admin/admin-ajax.php',
      type: 'POST',
      dataType: 'json',
    };
  }

  /**
   * Get common request data
   * @returns {object} Common data for AJAX requests
   */
  getCommonRequestData() {
    return {
      posts_per_page: this.postsPerPage,
      category: this.currentCategory,
      topic: this.currentTopic,
      sort: this.currentSort,
      post_type: this.postType,
      page_id: this.pageId,
    };
  }

  /**
   * Toggle load more button visibility
   * @param {boolean} show - Whether to show the button
   */
  toggleLoadMoreButton(show) {
    if (show) {
      this.$loadMoreBtn.removeClass(AdditionalPostGrid.CLASSES.HIDDEN);
      if (this.$loadMoreWrapper.length) {
        this.$loadMoreWrapper.removeClass(AdditionalPostGrid.CLASSES.HIDDEN);
      }
    } else {
      this.$loadMoreBtn.addClass(AdditionalPostGrid.CLASSES.HIDDEN);
      if (this.$loadMoreWrapper.length) {
        this.$loadMoreWrapper.addClass(AdditionalPostGrid.CLASSES.HIDDEN);
      }
    }
  }

  /**
   * Show content and hide empty message
   */
  showContent() {
    this.$content.removeClass(AdditionalPostGrid.CLASSES.HIDDEN);
    this.$empty.addClass(AdditionalPostGrid.CLASSES.HIDDEN);
  }

  /**
   * Hide content and show empty message
   */
  showEmpty() {
    this.$content.addClass(AdditionalPostGrid.CLASSES.HIDDEN);
    this.$empty.removeClass(AdditionalPostGrid.CLASSES.HIDDEN);
  }

  /**
   * Update max pages from response
   * @param {number} maxPages - Maximum number of pages
   */
  updateMaxPages(maxPages) {
    if (maxPages) {
      this.maxPages = maxPages;
      this.$el.data('max-pages', maxPages);
    }
  }

  /**
   * Update current page
   * @param {number} page - Current page number
   */
  updateCurrentPage(page) {
    this.currentPage = page;
    this.$loadMoreBtn.data('page', page);
  }

  /**
   * Refresh posts with current filters
   */
  refreshPosts() {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    LoadingManager.show();

    // Update clear filters button state
    const hasActiveFilters = this.currentTopic || this.currentSort;
    this.$clearFiltersBtn.prop('disabled', !hasActiveFilters);

    $.ajax({
      ...this.getAjaxBaseConfig(),
      data: {
        action: AdditionalPostGrid.ACTIONS.FILTER_SORT,
        ...this.getCommonRequestData(),
        keywords: this.currentSearch,
        excluded_ids: this.excludedIds,
        page: 1,
      },
      success: (response) => this.handleRefreshSuccess(response),
      error: (error) => this.handleRefreshError(error),
      complete: () => {
        this.isLoading = false;
        LoadingManager.hide();
      },
    });
  }

  /**
   * Handle successful refresh response
   * @param {object} response - AJAX response
   */
  handleRefreshSuccess(response) {
    const hasResults =
      response.success &&
      response.data.html &&
      response.data.html.trim() !== '';

    if (hasResults) {
      // Update grid with new content
      this.$grid.html(response.data.html);
      this.updateCurrentPage(1);
      this.showContent();

      // Update pagination
      this.updateMaxPages(response.data.max_pages);

      // Determine if load more button should be visible
      const shouldShowLoadMore = response.data.has_more && this.maxPages > 1;
      this.toggleLoadMoreButton(shouldShowLoadMore);

      // Update search heading
      const totalPosts =
        response.data.total_posts ||
        (response.data.post_ids && response.data.post_ids.length) ||
        0;

      this.updateSearchHeadingWithCount(this.currentSearch, totalPosts);
    } else {
      // No results - show empty state
      this.showEmpty();
      this.$grid.html('');
      this.updateSearchHeadingWithCount(this.currentSearch, 0);
    }
  }

  /**
   * Handle refresh error
   * @param {object} error - Error object
   */
  handleRefreshError(error) {
    console.error('Error filtering/sorting news:', error);
  }

  /**
   * Load more posts (pagination)
   */
  loadMore() {
    if (this.isLoading || this.currentPage >= this.maxPages) {
      return;
    }

    this.isLoading = true;
    this.$loadMoreBtn.addClass(AdditionalPostGrid.CLASSES.LOADING);
    LoadingManager.show();

    const nextPage = this.currentPage + 1;

    $.ajax({
      ...this.getAjaxBaseConfig(),
      data: {
        action: AdditionalPostGrid.ACTIONS.LOAD_MORE,
        ...this.getCommonRequestData(),
        search: this.currentSearch,
        page: nextPage,
      },
      success: (response) => this.handleLoadMoreSuccess(response, nextPage),
      error: (error) => this.handleLoadMoreError(error),
      complete: () => {
        this.isLoading = false;
        this.$loadMoreBtn.removeClass(AdditionalPostGrid.CLASSES.LOADING);
        LoadingManager.hide();
      },
    });
  }

  /**
   * Handle successful load more response
   * @param {object} response - AJAX response
   * @param {number} nextPage - Next page number
   */
  handleLoadMoreSuccess(response, nextPage) {
    if (!response.success) {
      console.error('Load more failed:', response);
      this.toggleLoadMoreButton(false);
      return;
    }

    const hasMoreResults =
      response.data.html && response.data.html.trim() !== '';

    if (hasMoreResults) {
      // Append new posts to grid
      this.$grid.append(response.data.html);
      this.updateCurrentPage(nextPage);

      // Update pagination
      this.updateMaxPages(response.data.max_pages);

      // Hide button if no more posts
      const shouldShowLoadMore =
        response.data.has_more && this.currentPage < this.maxPages;
      this.toggleLoadMoreButton(shouldShowLoadMore);
    } else {
      // No more results
      this.toggleLoadMoreButton(false);
    }
  }

  /**
   * Handle load more error
   * @param {object} error - Error object
   */
  handleLoadMoreError(error) {
    console.error('Error loading more news:', error);
  }
}

/**
 * Static 'constants' (old syntax for broad compatibility)
 * (Placed after class to avoid class-fields syntax)
 */
AdditionalPostGrid.ACTIONS = {
  FILTER_SORT: 'filter_sort_news_posts',
  LOAD_MORE: 'load_more_filtered_news',
};

AdditionalPostGrid.CLASSES = {
  HIDDEN: 'hidden',
  LOADING: 'loading',
};

// Initialize on page load
if (typeof window.acf === 'undefined') {
  $(document).ready(() => {
    $('.gg-additional-post-grid').each((_index, el) => {
      new AdditionalPostGrid(el).init();
    });
  });
} else {
  acf.addAction(
    'render_block_preview/type=acf/gravity-additional-post-grid',
    ($el) => {
      $el.find('.gg-additional-post-grid').each((_index, el) => {
        new AdditionalPostGrid(el).init();
      });
    },
  );
}
